import OrderCalc from 'holocron/order-calc';

import buildPricingContext from 'Redux/reselects/order-calc/build-pricing-context';

// The report must not stop the caller, for example when the reporter needs
// `window` and this runs on the server
const safeReport = (reportError, error, extra) => {
  try {
    reportError(error, extra);
  } catch {
    // Ignore: the result is more important than the report
  }
};

/**
 * The cart through holocron order-calc (spec §10):
 * buildPricingContext → OrderCalc.calculate.
 *
 * When the builder throws, or calculate() returns ok: false
 * (INVALID_CONTEXT), this function reports the error and returns it. It never
 * throws. If the report throws, it still returns the error.
 *
 * @param   {Object}   input
 * @param   {Object}   input.cart          state.cart
 * @param   {Object[]} input.pickAndMixAll state.pickAndMix.all
 * @param   {Object}   input.voucher       state.voucher.voucher
 * @param   {string}   input.currencyCode  ISO 4217
 * @param   {string}   input.countryCode   ISO 3166-1 alpha-2
 * @param   {number}   input.now           Epoch milliseconds
 * @param   {Function} input.reportError   (error, extra) => void
 * @returns {Object}                       { order, error, now }. `order` is
 *                                         result.order (integer minor units)
 *                                         and `error` is null, or `order` is
 *                                         null and `error` is an Error.
 *                                         `now` is the time of the context.
 */
export default function calculateOrder({
  cart,
  pickAndMixAll,
  voucher,
  currencyCode,
  countryCode,
  now,
  reportError,
}) {
  let error;
  let extra;

  try {
    const context = buildPricingContext({
      cart,
      pickAndMixAll,
      voucher,
      currencyCode,
      countryCode,
      now,
    });
    const result = OrderCalc.calculate(context);

    if (result.ok) {
      return { order: result.order, error: null, now };
    }

    error = new Error(`order-calc ${result.error.code}`);
    extra = { code: result.error.code, issues: result.error.issues };
  } catch (err) {
    error = err;
    extra = {};
  }

  safeReport(reportError, error, extra);

  return { order: null, error, now };
}
